import { CartUpdateEvent, ThemeEvents } from '@theme/events';
import { fetchConfig } from '@theme/utilities';

/**
 * Automatic free gifts from the `free_gift_code` metaobjects (see snippets/free-gift-codes-data.liquid).
 * When the cart holds enough valid products, the gift product is added; when it stops qualifying, the gift is
 * removed. No Shopify discount is involved, so the gift is charged at whatever price it has in the admin.
 * Gift lines are locked in the cart UI (see snippets/cart-products.liquid).
 *
 * @typedef {Object} FreeGift
 * @property {string} code - Identifies the gift; stored on its cart line.
 * @property {number} required_quantity - How many valid products the cart needs.
 * @property {{ id: number }[]} valid_products - Products that count towards the required quantity.
 * @property {{ variant_id: number | null; available: boolean | null }} free_product - The gift product.
 */

/**
 * @typedef {{ key: string; product_id: number; variant_id: number; quantity: number; properties: Record<string, string> | null }} CartLine
 */

/**
 * @typedef {{ items: CartLine[]; item_count: number }} Cart
 */

/** Hidden line item property marking a gift line and the gift it belongs to. */
const FREE_GIFT_PROPERTY = '_free_gift_code';

const SYNC_SOURCE = 'free-gift-sync';

/**
 * Returns every free gift rendered on the page.
 * @returns {FreeGift[]}
 */
function getFreeGifts() {
  const dataElement = document.getElementById('FreeGiftCodes');
  if (!dataElement) return [];

  try {
    return JSON.parse(dataElement.textContent || '[]');
  } catch {
    return [];
  }
}

/**
 * Fetches the cart.
 * @returns {Promise<Cart>}
 */
async function fetchCart() {
  const response = await fetch(`${Theme.routes.cart_url}.js`);
  return response.json();
}

/**
 * Returns whether the cart has enough valid products for a gift. Gift lines don't count.
 * @param {FreeGift} gift - The gift.
 * @param {CartLine[]} lines - The cart lines.
 * @returns {boolean}
 */
function cartQualifies(gift, lines) {
  const validProductIds = new Set(gift.valid_products.map((product) => product.id));
  let validQuantity = 0;
  for (const line of lines) {
    if (validProductIds.has(line.product_id) && !line.properties?.[FREE_GIFT_PROPERTY]) {
      validQuantity += line.quantity;
    }
  }
  return validQuantity >= gift.required_quantity;
}

/**
 * Brings the cart's gift lines in line with what the cart qualifies for: adds missing gifts, removes gifts
 * the cart no longer qualifies for (plus duplicates and gift products added by other means), and keeps
 * each gift at quantity 1.
 */
async function syncFreeGifts() {
  const gifts = getFreeGifts();
  const giftVariantIds = new Set(gifts.map((gift) => gift.free_product.variant_id));
  const cart = await fetchCart();

  /** @type {Record<string, number>} */
  const updates = {};
  /** @type {Set<string>} */
  const keptGifts = new Set();

  for (const line of cart.items) {
    const lineGift = line.properties?.[FREE_GIFT_PROPERTY]?.toUpperCase();

    if (!lineGift) {
      if (giftVariantIds.has(line.variant_id)) updates[line.key] = 0;
      continue;
    }

    const gift = gifts.find((entry) => entry.code === lineGift);
    if (!gift || keptGifts.has(lineGift) || !cartQualifies(gift, cart.items)) {
      updates[line.key] = 0;
    } else {
      keptGifts.add(lineGift);
      if (line.quantity !== 1) updates[line.key] = 1;
    }
  }

  const giftsToAdd = gifts.filter(
    (gift) =>
      gift.free_product.variant_id &&
      gift.free_product.available &&
      !keptGifts.has(gift.code) &&
      cartQualifies(gift, cart.items)
  );

  if (Object.keys(updates).length === 0 && giftsToAdd.length === 0) return;

  if (Object.keys(updates).length > 0) {
    await fetch(Theme.routes.cart_update_url, {
      ...fetchConfig('json', { body: JSON.stringify({ updates }) }),
    });
  }

  if (giftsToAdd.length > 0) {
    await fetch(Theme.routes.cart_add_url, {
      ...fetchConfig('json', {
        body: JSON.stringify({
          items: giftsToAdd.map((gift) => ({
            id: gift.free_product.variant_id,
            quantity: 1,
            properties: { [FREE_GIFT_PROPERTY]: gift.code },
          })),
        }),
      }),
    });
  }

  const updatedCart = await fetchCart();
  document.dispatchEvent(
    new CartUpdateEvent(updatedCart, SYNC_SOURCE, {
      itemCount: updatedCart.item_count,
      source: SYNC_SOURCE,
    })
  );
}

/** @type {Promise<void> | null} */
let activeSync = null;
let syncQueued = false;

/**
 * Runs a sync, queueing one follow-up run if the cart changes mid-sync.
 */
async function queueSync() {
  if (activeSync) {
    syncQueued = true;
    return;
  }

  activeSync = syncFreeGifts().catch(() => {});
  await activeSync;
  activeSync = null;

  if (syncQueued) {
    syncQueued = false;
    queueSync();
  }
}

/**
 * Syncs gift lines after any cart or discount change, skipping updates this module made itself.
 * @param {Event} event - The cart or discount update event.
 */
function onCartChange(event) {
  // Keep syncing with no active gifts too, so gift lines from a deactivated gift are removed
  if (!document.getElementById('FreeGiftCodes')) return;
  if (event instanceof CartUpdateEvent && event.detail.data?.source === SYNC_SOURCE) return;

  queueSync();
}

// Modules run once per page, so these listeners are only bound once even if several files import this module.
document.addEventListener(ThemeEvents.cartUpdate, onCartChange);
document.addEventListener(ThemeEvents.discountUpdate, onCartChange);

// The data snippet flags carts whose gift lines are out of sync, e.g. a cart that qualified on a previous visit.
if (document.getElementById('FreeGiftCodes')?.dataset.cartNeedsGiftSync === 'true') queueSync();
